import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import Modal from './Modal';
import './PromptDialog.css';

const PromptContext = createContext(null);

export const usePrompt = () => {
  const context = useContext(PromptContext);
  if (!context) {
    throw new Error('usePrompt must be used within a PromptProvider');
  }
  return context;
};

export const PromptProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);
  const resolveRef = useRef(null);
  const inputRef = useRef(null);

  const prompt = useCallback(({ title, message, label, defaultValue = '', placeholder = '', confirmLabel = 'Save', cancelLabel = 'Cancel', variant = 'default' } = {}) => {
    return new Promise((resolve) => {
      // If a previous prompt is still pending, settle it as cancelled —
      // otherwise its promise never resolves and its awaiter hangs forever.
      if (resolveRef.current) resolveRef.current(null);
      resolveRef.current = resolve;
      setDialog({ title, message, label, defaultValue, placeholder, confirmLabel, cancelLabel, variant });
    });
  }, []);

  const close = useCallback((value) => {
    setDialog(null);
    if (resolveRef.current) {
      resolveRef.current(value);
      resolveRef.current = null;
    }
  }, []);

  const handleConfirm = useCallback(() => {
    close(inputRef.current ? inputRef.current.value : '');
  }, [close]);

  const handleCancel = useCallback(() => {
    close(null);
  }, [close]);

  // Auto-focus the input when the dialog opens.
  useEffect(() => {
    if (dialog && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [dialog]);

  // Submit on Enter, cancel on Escape is handled by Modal.
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleConfirm();
    }
  };

  return (
    <PromptContext.Provider value={prompt}>
      {children}
      {dialog && (
        <Modal onClose={handleCancel} titleId="prompt-title" dialogClassName="modal-dialog--sm">
          <div className="prompt-dialog">
            {dialog.title && (
              <div id="prompt-title" className="prompt-title">
                <i className={`fas ${dialog.variant === 'danger' ? 'fa-exclamation-triangle' : 'fa-edit'}`} aria-hidden="true" />
                {dialog.title}
              </div>
            )}
            {dialog.message && <div className="prompt-message">{dialog.message}</div>}
            {dialog.label && <label htmlFor="prompt-input" className="prompt-label">{dialog.label}</label>}
            <input
              id="prompt-input"
              ref={inputRef}
              type="text"
              defaultValue={dialog.defaultValue}
              placeholder={dialog.placeholder}
              onKeyDown={handleKeyDown}
              maxLength="200"
              className="prompt-input form-control"
              aria-describedby={dialog.message ? 'prompt-desc' : undefined}
            />
            {dialog.message && <span id="prompt-desc" className="sr-only">{dialog.message}</span>}
            <div className="prompt-actions">
              <button className="btn btn-secondary" onClick={handleCancel}>
                {dialog.cancelLabel}
              </button>
              <button className={`btn ${dialog.variant === 'danger' ? 'btn-danger' : 'btn-primary'}`} onClick={handleConfirm}>
                {dialog.confirmLabel}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </PromptContext.Provider>
  );
};

export default PromptProvider;